import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProducts, getProductCategories } from '../../api/products';
import ProductModal from '../../components/features/ProductModal';
import UnifiedInquirySection from '../../components/features/UnifiedInquirySection';
import './Products.css';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([{ id: 'ALL', label: '전체' }]);
    const [loading, setLoading] = useState(true);
    const [topTab, setTopTab] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);

    const navigate = useNavigate();
    const { id } = useParams();

    // Sync Modal state with URL Route
    useEffect(() => {
        if (id && products.length > 0) {
            const targetProduct = products.find(p => p.id === id);
            if (targetProduct) {
                setSelectedProduct(targetProduct);
            }
        } else if (!id && selectedProduct) {
            setSelectedProduct(null);
        }
    }, [id, products]);

    // Browser tab title — same pattern as UseCases: Layout skips the detail
    // path, so the page owns the title once the modal is open.
    useEffect(() => {
        if (selectedProduct?.name) {
            document.title = `${selectedProduct.name} | 제품 및 규격 | 가까운솔루션`;
        } else if (!id) {
            document.title = '제품 및 규격 | 가까운솔루션';
        }
    }, [selectedProduct, id]);

    // Initial Data Loading
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [productsData, categoriesData] = await Promise.all([
                getProducts(),
                getProductCategories()
            ]);
            setProducts(productsData);

            // Transform and prepend 'ALL'
            const formattedCategories = [
                { id: 'ALL', label: '전체' },
                ...categoriesData.sort((a, b) => a.order - b.order) // Ensure correct order
            ];
            setCategories(formattedCategories);

        } catch (error) {
            console.error("Failed to load data", error);
        } finally {
            setLoading(false);
        }
    };

    // Filtering Logic
    const filteredProducts = products.filter(product => {
        // 1. Category Filter
        // Check against both ID and Code for backward compatibility
        if (topTab !== 'ALL') {
            const selectedCat = categories.find(c => c.id === topTab);
            // Match product category (which might be ID or Code) against selected category's ID or Code
            // Use String() coercion since APIs often return DB integers as strings
            const isMatch = String(product.category) === String(selectedCat?.id) || 
                            String(product.category) === String(selectedCat?.code);
            if (!isMatch) {
                return false;
            }
        }

        // 2. Search Filter (Name, Description, Specs)
        if (!searchTerm) return true;

        const term = searchTerm.toLowerCase();
        const inName = product.name?.toLowerCase().includes(term);
        const inDesc = product.description?.toLowerCase().includes(term);

        // Search in Specs Values
        const inSpecs = product.specs
            ? Object.values(product.specs).some(val => val.toLowerCase().includes(term))
            : false;

        return inName || inDesc || inSpecs;
    });

    return (
        <div className="products-page">
            <div className="page-hero">
                <div className="container">
                    <h1 className="page-title">제품 및 규격</h1>
                    <p className="page-subtitle">
                        다양한 환경에 최적화된 가까운솔루션의 제품 라인업을 확인하세요.
                    </p>
                </div>
            </div>

            <div className="container">
                {/* Search & Tabs Container */}
                <div className="controls-container">
                    {/* Category Tabs */}
                    <div className="category-tabs">
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                className={`cat-btn ${topTab === cat.id ? 'active' : ''}`}
                                onClick={() => setTopTab(cat.id)}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {/* Search Bar */}
                    <div className="search-bar-wrapper">
                        <input
                            type="text"
                            placeholder="제품명, 규격 검색..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="product-search-input"
                        />
                        <span className="search-icon">🔍</span>
                    </div>
                </div>

                {/* Product Grid */}
                {loading ? (
                    <div className="loading-state">Loading...</div>
                ) : (
                    <div className="products-grid">
                        {filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                                <div
                                    key={product.id}
                                    className="product-card"
                                    onClick={() => navigate(`/products/${product.id}`)}
                                >
                                    {/* Image Area */}
                                    <div
                                        className="product-thumbnail"
                                        style={{ backgroundImage: `url(${product.imageUrl || '/default-product.png'})` }}
                                    />

                                    {/* Content Area */}
                                    <div className="product-info">
                                        <div className="product-category-badge">
                                            {categories.find(c => c.id === product.category || c.code === product.category || c.id === Number(product.category))?.label || product.category}
                                        </div>
                                        <h3 className="product-name">{product.name}</h3>
                                        <p className="product-desc">{product.description}</p>
                                        <div className="view-details-link">상세 규격 보기 &rarr;</div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-results">검색 결과가 없습니다.</div>
                        )}
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <ProductModal
                isOpen={!!selectedProduct}
                onClose={() => navigate('/products')}
                product={selectedProduct}
            />

            {/* Unified Inquiry Section */}
            <div style={{ marginTop: '4rem' }}>
                <UnifiedInquirySection title="자세한 기능과 스펙이 궁금하신가요?" buttonText="📋 표준 제안서 다운로드" buttonAction="PROPOSAL" />
            </div>
        </div>
    );
};

export default Products;
